"use client";

import { useTranslations } from "next-intl";
import { useTransition } from "react";
import { stopImpersonation } from "@/app/actions/impersonate";
import { toast } from "@/ui/toast";

export function ImpersonationBanner({
  currentEmail,
  originalEmail,
}: {
  currentEmail: string | null;
  originalEmail: string;
}) {
  const t = useTranslations("admin.impersonation");
  const [isPending, startTransition] = useTransition();

  const onStop = () => {
    startTransition(async () => {
      try {
        const ok = await stopImpersonation();
        if (ok) {
          // Stay on the current URL — server-side layouts re-gate the route
          // against the now-restored operator and redirect if needed (e.g.
          // /app/* → /admin for an admin who came back from a tenant session).
          window.location.reload();
        }
      } catch (e) {
        toast.error(t("stopFailed"), { description: (e as Error).message });
      }
    });
  };

  return (
    <div className="fixed inset-x-0 top-0 z-[60] flex h-9 items-center justify-between gap-3 border-b border-amber-500/60 bg-amber-500 px-4 text-sm text-amber-950 dark:bg-amber-600 dark:text-amber-50">
      <span>
        {t("activeAs", {
          current: currentEmail ?? "-",
          original: originalEmail,
        })}
      </span>
      <button
        type="button"
        onClick={onStop}
        disabled={isPending}
        className="rounded-md border border-amber-700/40 px-3 py-1 text-xs font-medium hover:bg-amber-600/20 disabled:opacity-50 dark:border-amber-400/40 dark:hover:bg-amber-500/20"
      >
        {isPending ? t("returning") : t("returnTo", { email: originalEmail })}
      </button>
    </div>
  );
}
